import React, { useState } from "react";
import { format, parseISO, isValid } from "date-fns";
import { az } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

/**
 * DatePickerAZ — Azerbaijani-locale date picker.
 * Emits/expects ISO yyyy-MM-dd strings (compatible with native <input type="date">).
 *
 * Props:
 *  - value: "yyyy-MM-dd" or ""
 *  - onChange(iso: string)
 *  - placeholder
 *  - testid (data-testid for the trigger button)
 *  - className
 *  - disabled
 *  - minDate / maxDate: optional Date objects
 */
export default function DatePickerAZ({
  value,
  onChange,
  placeholder = "Tarix seçin",
  testid,
  className,
  disabled = false,
  minDate,
  maxDate,
}) {
  const [open, setOpen] = useState(false);

  const date = value ? parseISO(value) : undefined;
  const displayed = date && isValid(date)
    ? format(date, "d MMMM yyyy", { locale: az })
    : "";

  const handleSelect = (d) => {
    if (!d) {
      onChange?.("");
    } else {
      onChange?.(format(d, "yyyy-MM-dd"));
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full justify-start text-left font-normal h-10 bg-white",
            !displayed && "text-slate-400",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">{displayed || placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          initialFocus
          locale={az}
          disabled={(d) => {
            if (minDate && d < minDate) return true;
            if (maxDate && d > maxDate) return true;
            return false;
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
